import { CopyIcon } from "lucide-react";
import React, { useContext } from "react";

import InputAdornment from "@mui/material/InputAdornment";

import TextField from "@/components/styled/TextField";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/UserContext";
// sendStandardEvent is a plain firebase logger, not a hook, despite the
// "use-firebase-event" file path (matching the usage in CodeSnippet.tsx).
import sendStandardEvent from "@/hooks/use-firebase-event";
import { copyToClipboard } from "@/lib/clipboard";
import { pushError, pushSuccess } from "@/slices/notifications";
import { useDispatch, useSelector } from "@/store";

export type ClusterIdFieldProps = {
  clusterId?: string;
};

export const ClusterIdField = ({ clusterId }: ClusterIdFieldProps) => {
  const dispatch = useDispatch();
  const { user } = useContext(UserContext);
  const currentClusterId = useSelector((state) => state.current.clusterId);
  const id = clusterId ?? currentClusterId ?? "";

  const handleCopy = async (): Promise<void> => {
    if (!id) {
      return;
    }
    const ok = await copyToClipboard(id);
    if (!ok) {
      dispatch(pushError("Failed to copy cluster ID"));
      return;
    }
    sendStandardEvent("copied_clusterId", "copying", user);
    dispatch(pushSuccess("Copied"));
  };

  return (
    <TextField
      fullWidth
      value={id}
      placeholder="Unavailable"
      sx={{
        "& input": {
          fontFamily:
            'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
        },
      }}
      inputProps={{
        "aria-label": "Cluster ID",
        "data-testid": "cluster-id-value",
      }}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Button
              type="button"
              variant="secondary"
              aria-label="Copy Cluster ID"
              onClick={handleCopy}
              disabled={!id}
              className="border"
            >
              <CopyIcon />
            </Button>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default ClusterIdField;
